import { DailyAudio, DailyProvider, useCallObject } from '@daily-co/daily-react'
import { trackEvent } from '@lib/clinician-tracking'
import { useEffect, useState } from 'react'

export type BlueprintDailyRoomConfiguration = {
  url: string
  token?: string
  isResuming?: boolean
  isOwner?: boolean
}

export const useBlueprintDailyCall = (initWithoutRoom: boolean = false) => {
  const [isInCall, setIsInCall] = useState(false)
  const [isCallEnded, setIsCallEnded] = useState(false)
  const [isInWaitingRoom, setIsInWaitingRoom] = useState(false)
  const [hasLeftCall, setHasLeftCall] = useState(false)
  const [isRoomCreated, setIsRoomCreated] = useState(false)
  const [isBackgroundBlurred, setIsBackgroundBlurred] = useState(false)
  const [hasTranscriptionError, setHasTranscriptionError] = useState(false)

  const [roomConfiguration, setRoomConfiguration] =
    useState<BlueprintDailyRoomConfiguration>({
      url: '',
      token: undefined,
      isResuming: false,
      isOwner: false
    })

  const callObject = useCallObject({
    options: {
      allowMultipleCallInstances: false,
      url: roomConfiguration.url,
      token: roomConfiguration.isOwner ? roomConfiguration.token : '',
      dailyConfig: {
        useDevicePreferenceCookies: true
      }
    },
    shouldCreateInstance() {
      // Only check for token if user is the room owner
      const tokenCheck = roomConfiguration.isOwner
        ? !!roomConfiguration.token
        : true

      // wait for valid roomUrl/Token to be provided before creating instance
      const shouldCreate =
        tokenCheck &&
        (initWithoutRoom || !!roomConfiguration.url) &&
        !isRoomCreated

      // Ensure we only ever create the room once, Daily's react hook is weird for this and will fire every single time the context changes, so need to force this.
      if (shouldCreate) {
        setIsRoomCreated(true)
      }

      return shouldCreate
    }
  })

  useEffect(() => {
    const isBackgroundBlurEnabled =
      localStorage.getItem('telehealth_background_blur') === 'true'
    setIsBackgroundBlurred(isBackgroundBlurEnabled)
  }, [])

  useEffect(() => {
    if (!callObject) return

    const newBlurSetting = !isBackgroundBlurred
      ? { type: 'none' }
      : { type: 'background-blur', config: { strength: 0.9 } }

    callObject.updateInputSettings({
      video: {
        processor: newBlurSetting as any
      }
    })
  }, [isBackgroundBlurred, callObject])

  const toggleBackgroundBlur = () => {
    const newValue = !isBackgroundBlurred
    setIsBackgroundBlurred(newValue)
    localStorage.setItem(
      'telehealth_background_blur',
      newValue ? 'true' : 'false'
    )
  }

  const _sharedOnJoinCall = () => {
    setIsInWaitingRoom(false)
    setIsInCall(true)

    callObject?.on('error', event => {
      // Backend ejects users from call when clinician ends the session, this comes through on the "error" event for some reason
      if (event.error?.type === 'ejected') {
        setIsCallEnded(true)
      }
    })

    callObject?.on('left-meeting', f => {
      trackEvent('event:telehealth-session.participant-left-call')
      setIsInCall(false)
      setIsInWaitingRoom(true)
    })

    callObject?.on('transcription-error', f => {
      setHasTranscriptionError(true)
    })
  }

  const leaveCall = async () => {
    await callObject?.leave()
    await callObject?.destroy()
    setIsInCall(false)
    setIsInWaitingRoom(true)
    setHasLeftCall(true)
  }

  useEffect(() => {
    return () => {
      if (callObject) {
        callObject.destroy()
      }
    }
  }, [callObject])

  return {
    callObject,
    isInCall,
    isInWaitingRoom,
    isCallEnded,
    hasLeftCall,
    roomConfiguration,
    setRoomConfiguration,
    setIsInCall,
    setIsInWaitingRoom,
    setIsCallEnded,
    setHasLeftCall,
    isBackgroundBlurred,
    toggleBackgroundBlur,
    _sharedOnJoinCall,
    leaveCall,
    hasTranscriptionError
  }
}
